// Manual-mode sketch geometry; start/finish mirror the planner's endpoint fields.
import * as turf from "@turf/turf"

const COORD_EPSILON = 1e-9

export const isManualDraft = (route) =>
  !!route && (route.sketch === true || route.optimisedFor === "manual")

export const legtypeOf = (feature) =>
  String(
    feature?.properties?.legtype ?? feature?.properties?.legType ?? "greatcircle",
  ).toLowerCase() === "rhumbline"
    ? "rhumbline"
    : "greatcircle"

// A compute adds calculated points; only the original points are user-set.
export function manualRoutePoints(route) {
  return (route?.points?.geojson?.features ?? []).filter(
    (f) => f?.properties?.type === "original_point",
  )
}

export function manualMiddlePoints(route) {
  const points = manualRoutePoints(route)
  return points.length > 2 ? points.slice(1, -1) : []
}

export function manualStartLegtype(route) {
  const first = manualRoutePoints(route)[0]
  return first ? legtypeOf(first) : "greatcircle"
}

// Longitudes compare modulo 360: the analyser echoes wrapped coords, endpoints stay as clicked.
export const sameLonLat = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  Math.abs(((((Number(a[0]) - Number(b[0])) % 360) + 540) % 360) - 180) <
    COORD_EPSILON &&
  Math.abs(Number(a[1]) - Number(b[1])) < COORD_EPSILON

export function manualEndpointsMatch(route, start, finish) {
  const points = manualRoutePoints(route)
  if (points.length < 2) return false
  return (
    sameLonLat(points[0].geometry?.coordinates, start) &&
    sameLonLat(points.at(-1).geometry?.coordinates, finish)
  )
}

// Each point's legtype describes its outgoing leg; the finish has none.
export function buildManualFeatures({
  start,
  middles = [],
  finish,
  startLegtype = "greatcircle",
}) {
  const points = [
    { coords: start, legtype: startLegtype },
    ...middles.map((f) => ({
      coords: f?.geometry?.coordinates ?? f?.coords,
      legtype: f?.legtype ?? legtypeOf(f),
    })),
    { coords: finish, legtype: "greatcircle" },
  ]
  return points.map(({ coords, legtype }, idx) =>
    turf.point(coords, {
      legtype,
      legType: legtype,
      type: "original_point",
      number: idx + 1,
      extended_number: 0,
    }),
  )
}

// Start, middles and finish as {coords, legtype}; a missing sketch yields the endpoints only.
export function manualLegPoints(route, { start, finish }) {
  return [
    { coords: start, legtype: manualStartLegtype(route) },
    ...manualMiddlePoints(route).map((f) => ({
      coords: f.geometry.coordinates,
      legtype: legtypeOf(f),
    })),
    { coords: finish, legtype: "greatcircle" },
  ]
}

export const manualPointLabel = (count, index) =>
  index === 0 ? "Start" : index === count - 1 ? "Finish" : `WP ${index + 1}`

const LEG_SAMPLES = 64

function legSamples(a, b, legtype) {
  if (legtype !== "greatcircle") return [a, b]
  try {
    const gc = turf.greatCircle(turf.point(a), turf.point(b), { npoints: LEG_SAMPLES })
    return gc.geometry.type === "MultiLineString"
      ? gc.geometry.coordinates.flat()
      : gc.geometry.coordinates
  } catch {
    return [a, b]
  }
}

function unwrapLongitudes(coords) {
  const out = []
  for (const [lon, lat] of coords) {
    const prev = out.at(-1)?.[0]
    out.push([prev == null ? lon : lon + Math.round((prev - lon) / 360) * 360, lat])
  }
  return out
}

// The sampled legs only change with the points, not with the cursor.
const legSampleCache = new WeakMap()
function cachedLegSamples(points) {
  let legs = legSampleCache.get(points)
  if (!legs) {
    legs = points
      .slice(0, -1)
      .map((pt, i) => unwrapLongitudes(legSamples(pt.coords, points[i + 1].coords, pt.legtype)))
    legSampleCache.set(points, legs)
  }
  return legs
}

function shiftNear(coords, nearLon) {
  const mid = (coords[0][0] + coords.at(-1)[0]) / 2
  const shift = Math.round((nearLon - mid) / 360) * 360
  return coords.map(([lon, lat]) => [lon + shift, lat])
}

const segmentDistance = (p, a, b) => {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const lenSq = dx * dx + dy * dy
  const t = lenSq > 0
    ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq))
    : 0
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

// Leg (point i → i+1) closest to the click on screen; ties at a shared waypoint go to the smaller detour.
export function nearestManualLeg(points, click, project) {
  if (points.length < 2) return -1
  const p = project(click)
  let best = { index: -1, distance: Infinity, detour: Infinity }
  const legs = cachedLegSamples(points)
  for (let i = 0; i < legs.length; i++) {
    const samples = shiftNear(legs[i], click[0]).map(project)
    let distance = Infinity
    for (let k = 0; k < samples.length - 1; k++) {
      distance = Math.min(distance, segmentDistance(p, samples[k], samples[k + 1]))
    }
    const a = samples[0]
    const b = samples.at(-1)
    const detour =
      Math.hypot(p.x - a.x, p.y - a.y) +
      Math.hypot(p.x - b.x, p.y - b.y) -
      Math.hypot(b.x - a.x, b.y - a.y)
    if (
      distance < best.distance - 0.5 ||
      (Math.abs(distance - best.distance) <= 0.5 && detour < best.detour)
    ) {
      best = { index: i, distance, detour }
    }
  }
  return best.index
}

// The new waypoint splits leg `legIndex` and inherits its leg type.
export function insertManualWaypoint(route, { start, finish, point, legIndex }) {
  const legs = manualLegPoints(route, { start, finish })
  const middles = legs.slice(1, -1)
  middles.splice(legIndex, 0, { coords: point, legtype: legs[legIndex].legtype })
  return buildManualFeatures({ start, middles, finish, startLegtype: legs[0].legtype })
}

// Removing an endpoint promotes its neighbouring waypoint; null when there is none.
export function removeManualEndpoint(route, which, { start, finish }) {
  const middles = manualMiddlePoints(route)
  if (middles.length === 0) return null
  if (which === "start") {
    const [nextStart, ...rest] = middles
    return {
      endpoint: nextStart.geometry.coordinates,
      features: buildManualFeatures({
        start: nextStart.geometry.coordinates,
        middles: rest,
        finish,
        startLegtype: legtypeOf(nextStart),
      }),
    }
  }
  const nextFinish = middles.at(-1)
  return {
    endpoint: nextFinish.geometry.coordinates,
    features: buildManualFeatures({
      start,
      middles: middles.slice(0, -1),
      finish: nextFinish.geometry.coordinates,
      startLegtype: manualStartLegtype(route),
    }),
  }
}

export function setManualStartLegtype(route, legtype, { start, finish }) {
  return buildManualFeatures({
    start,
    middles: manualMiddlePoints(route),
    finish,
    startLegtype: legtype,
  })
}
